import React, { useState } from 'react';
import { AlertCircle, RefreshCw, XCircle, DollarSign, Clock, CheckCircle, User, Calendar } from 'lucide-react';
import { formatMeetingTime } from '../../utils/timezone';

interface ConsultationRequest {
  id: string;
  requestId: string;
  patientName: string;
  patientEmail: string;
  patientPhone?: string;
  symptoms: string[];
  status: string;
  paymentStatus?: string;
  paymentIntentId?: string;
  requestedDate: string;
  requestedSpecificTime?: string; // ✅ NEW: Specific time
  requestedTimeRangeStart?: string; // DEPRECATED
  requestedTimeRangeEnd?: string; // DEPRECATED
  timezone?: string;
  createdAt: string;
  amount?: number;
}

interface ConsultationRequestsManagerProps {
  requests: ConsultationRequest[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const ConsultationRequestsManager: React.FC<ConsultationRequestsManagerProps> = ({ 
  requests: initialRequests, 
  loading: initialLoading, 
  error: initialError,
  onRefresh 
}) => {
  const [requests] = useState<ConsultationRequest[]>(initialRequests);
  const [loading] = useState(initialLoading);
  const [error] = useState<string | null>(initialError);
  const [filter, setFilter] = useState<'all' | 'waiting_for_doctor_accepts' | 'doctor_accepted' | 'completed' | 'cancelled'>('waiting_for_doctor_accepts');
  const [processingId, setProcessingId] = useState<string | null>(null);

  console.log('🔍 ConsultationRequestsManager rendering - requests:', initialRequests.length, 'loading:', initialLoading);

  // Use props directly instead of state
  const currentRequests = initialRequests;
  const currentLoading = initialLoading;
  const currentError = initialError;

  const handleCancel = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to cancel this request? This will:\n\n1. Cancel the request\n2. Send email to patient explaining no doctor was found\n3. Update request status to cancelled\n\nNote: No payment to refund (promo code used)')) {
      return;
    }

    setProcessingId(requestId);
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod'}/admin/consultation-requests/${requestId}/cancel`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: 'No doctor available',
            sendEmail: true
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel request');
      }

      alert('✅ Request cancelled successfully. Email sent to patient.');
      onRefresh(); // Refresh list
    } catch (err: any) {
      console.error('Error cancelling request:', err);
      alert('❌ Error: ' + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelAndRefund = async (requestId: string) => {
    if (!window.confirm('Are you sure you want to cancel this request and refund the patient? This will:\n\n1. Cancel the payment hold (or refund if already charged)\n2. Send email to patient explaining no doctor was found and payment refunded\n3. Update request status to cancelled')) {
      return;
    }

    setProcessingId(requestId);
    
    try {
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod'}/admin/consultation-requests/${requestId}/cancel-refund`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reason: 'No doctor available',
            sendEmail: true
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel and refund');
      }

      alert('✅ Request cancelled and patient refunded successfully. Email sent to patient.');
      onRefresh(); // Refresh list
    } catch (err: any) {
      console.error('Error cancelling request:', err);
      alert('❌ Error: ' + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      'waiting_for_doctor_accepts': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: <Clock className="w-3 h-3" /> },
      'doctor_accepted': { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
      'completed': { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle className="w-3 h-3" /> },
      'expired': { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-3 h-3" /> },
      'cancelled': { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-3 h-3" /> },
    };

    const config = statusConfig[status] || statusConfig['waiting_for_doctor_accepts'];

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        {status.replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus?: string) => {
    if (!paymentStatus) return null;

    const statusConfig: Record<string, { bg: string; text: string }> = {
      'unpaid': { bg: 'bg-gray-100', text: 'text-gray-700' },
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      'paid': { bg: 'bg-green-100', text: 'text-green-700' },
      'refunded': { bg: 'bg-purple-100', text: 'text-purple-700' },
    };

    const config = statusConfig[paymentStatus] || { bg: 'bg-gray-100', text: 'text-gray-700' };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <DollarSign className="w-3 h-3" />
        {paymentStatus.toUpperCase()}
      </span>
    );
  };

  const filteredRequests = currentRequests.filter(req => {
    const status = req.status;
    
    if (filter === 'all') return true;
    if (filter === 'waiting_for_doctor_accepts') return status === 'waiting_for_doctor_accepts';
    if (filter === 'doctor_accepted') return status === 'doctor_accepted';
    if (filter === 'completed') return status === 'completed';
    if (filter === 'cancelled') return status === 'cancelled' || status === 'expired';
    
    return false;
  });

  // Sort: Pending requests with nearest requested time first, then by creation date
  const sortedRequests = [...filteredRequests].sort((a, b) => {
    const aStatus = a.status;
    const bStatus = b.status;
    
    // Priority 1: waiting_for_doctor_accepts first
    const aIsPending = aStatus === 'waiting_for_doctor_accepts';
    const bIsPending = bStatus === 'waiting_for_doctor_accepts';
    
    if (aIsPending && !bIsPending) return -1;
    if (!aIsPending && bIsPending) return 1;
    
    // Priority 2: For pending requests, sort by requested date/time (nearest first)
    if (aIsPending && bIsPending) {
      const aTime = a.requestedSpecificTime || a.requestedTimeRangeStart || '00:00';
      const bTime = b.requestedSpecificTime || b.requestedTimeRangeStart || '00:00';
      const aDateTime = new Date(`${a.requestedDate}T${aTime}:00`).getTime();
      const bDateTime = new Date(`${b.requestedDate}T${bTime}:00`).getTime();
      return aDateTime - bDateTime; // Nearest time first
    }
    
    // Priority 3: For non-pending, sort by creation date (newest first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  console.log('🔍 Filtered requests:', filteredRequests.length, 'Filter:', filter);
  console.log('🔍 All requests with statuses:', currentRequests.map(r => ({ id: r.id, status: r.status })));
  console.log('🔍 Unique statuses in data:', Array.from(new Set(currentRequests.map(r => r.status))));

  if (currentLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (currentError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{currentError}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Consultation Requests</h2>
          <p className="text-sm text-gray-600 mt-1">Manage all patient consultation requests</p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all', label: 'All' },
          { key: 'waiting_for_doctor_accepts', label: 'Waiting For Doctor Accepts' },
          { key: 'doctor_accepted', label: 'Doctor Accepted' },
          { key: 'completed', label: 'Completed' },
          { key: 'cancelled', label: 'Cancelled/Expired' }
        ].map((tab) => {
          const count = currentRequests.filter(r => {
            const status = r.status;
            
            if (tab.key === 'all') return true;
            if (tab.key === 'waiting_for_doctor_accepts') return status === 'waiting_for_doctor_accepts';
            if (tab.key === 'doctor_accepted') return status === 'doctor_accepted';
            if (tab.key === 'completed') return status === 'completed';
            if (tab.key === 'cancelled') return status === 'cancelled' || status === 'expired';
            return false;
          }).length;
          
          return (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symptoms</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedRequests.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No requests found
                  </td>
                </tr>
              ) : (
                sortedRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-gray-900">{request.requestId || request.id}</div>
                      <div className="text-xs text-gray-500">{new Date(request.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm text-gray-900">{request.patientName}</div>
                      <div className="text-xs text-gray-500">{request.patientEmail}</div>
                      {request.patientPhone && (
                        <div className="text-xs text-gray-500">{request.patientPhone}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {request.symptoms.slice(0, 2).map((symptom, idx) => (
                          <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                            {symptom}
                          </span>
                        ))}
                        {request.symptoms.length > 2 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            +{request.symptoms.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        // Handle different date/time formats from backend
                        const timezone = request.timezone || 'UTC';
                        
                        // If we have requestedDateTime (ISO timestamp), use it
                        if ((request as any).requestedDateTime) {
                          const { date, time, timezone: tzLabel } = formatMeetingTime((request as any).requestedDateTime, timezone);
                          return (
                            <div>
                              <div className="text-sm font-semibold text-gray-900">{date}</div>
                              <div className="text-lg font-bold text-blue-600">{time}</div>
                              <div className="text-xs text-gray-500">({tzLabel})</div>
                            </div>
                          );
                        }
                        
                        // Otherwise, show date + time range if available
                        return (
                          <div>
                            <div className="flex items-center gap-1 text-sm text-gray-900">
                              <Calendar className="w-3 h-3" />
                              {new Date(request.requestedDate).toLocaleDateString()}
                            </div>
                            {request.requestedSpecificTime ? (
                              <div className="text-xs text-gray-500">
                                {request.requestedSpecificTime.substring(0, 5)} (specific time)
                              </div>
                            ) : request.requestedTimeRangeStart && request.requestedTimeRangeEnd ? (
                              <div className="text-xs text-gray-500">
                                {request.requestedTimeRangeStart.substring(0, 5)} - {request.requestedTimeRangeEnd.substring(0, 5)}
                              </div>
                            ) : null}
                            {request.timezone && (
                              <div className="text-xs text-gray-500">({request.timezone.split('/').pop()?.replace(/_/g, ' ')} time)</div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-3">
                      {getPaymentStatusBadge(request.paymentStatus)}
                      {request.amount && (
                        <div className="text-xs text-gray-500 mt-1">
                          ${(request.amount / 100).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {request.status === 'waiting_for_doctor_accepts' ? (
                        request.paymentIntentId ? (
                          <button
                            onClick={() => handleCancelAndRefund(request.requestId || request.id)}
                            disabled={processingId === (request.requestId || request.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingId === (request.requestId || request.id) ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <DollarSign className="w-3 h-3" />
                                Cancel & Refund
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleCancel(request.requestId || request.id)}
                            disabled={processingId === (request.requestId || request.id)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white text-xs rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingId === (request.requestId || request.id) ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <XCircle className="w-3 h-3" />
                                Cancel (No Payment)
                              </>
                            )}
                          </button>
                        )
                      ) : request.status === 'doctor_accepted' && request.paymentIntentId ? (
                        <button
                          onClick={() => handleCancelAndRefund(request.requestId || request.id)}
                          disabled={processingId === (request.requestId || request.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {processingId === (request.requestId || request.id) ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <DollarSign className="w-3 h-3" />
                              Cancel & Refund
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">
                          {request.status === 'doctor_accepted' && !request.paymentIntentId && 'Accepted (Free)'}
                          {request.status === 'doctor_accepted' && request.paymentIntentId && 'Accepted'}
                          {request.status === 'completed' && 'Completed'}
                          {request.status === 'expired' && 'Expired'}
                          {request.status === 'cancelled' && 'Cancelled'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">Total Requests</div>
          <div className="text-2xl font-bold text-gray-900">{currentRequests.length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">Waiting For Doctor</div>
          <div className="text-2xl font-bold text-yellow-600">
            {currentRequests.filter(r => r.status === 'waiting_for_doctor_accepts').length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">Completed</div>
          <div className="text-2xl font-bold text-green-600">
            {currentRequests.filter(r => r.status === 'completed').length}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-600">Cancelled/Expired</div>
          <div className="text-2xl font-bold text-red-600">
            {currentRequests.filter(r => r.status === 'cancelled' || r.status === 'expired').length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationRequestsManager;
